"use client";

import { useContext, useEffect, useMemo } from "react";
import { TextField, Autocomplete } from "@mui/material";
import { FilterParamsContext } from "../FilterParamsProvider";
import { City, State } from "@/postgres";

export default function CitySelectClient({
  options,
  stateMap,
}: {
  options: City[];
  stateMap: Map<string, State>;
}) {
  const { filterParams, setFilterParams } = useContext(FilterParamsContext);
  const cityMap = useMemo(
    () =>
      new Map(
        options
          .filter((city) => {
            let shouldInclude = true;
            if (filterParams.states?.length) {
              shouldInclude = filterParams.states
                .map((id) => `${id}`)
                .includes(city.state_id);
            }
            if (filterParams.counties?.length) {
              shouldInclude =
                shouldInclude &&
                filterParams.counties
                  .map((id) => `${id}`)
                  .includes(city.county_id);
            }
            return shouldInclude;
          })
          .sort(
            (cityA, cityB) =>
              parseInt(cityA.state_id) - parseInt(cityB.state_id),
          )
          .map((city) => [city.id, city]),
      ),
    [filterParams.counties, filterParams.states, options],
  );

  useEffect(() => {
    // Remove cities that are no longer valid based on selected states/counties
    const validCityIds = new Set(cityMap.keys());
    const validFilterIds = filterParams.cities?.filter((id) =>
      validCityIds.has(`${id}`),
    );

    if (
      JSON.stringify(validFilterIds) !== JSON.stringify(filterParams.cities)
    ) {
      setFilterParams((prev) => ({
        ...prev,
        cities: validFilterIds,
      }));
    }
  }, [cityMap, filterParams.cities, setFilterParams]);

  return (
    <Autocomplete
      multiple
      options={Array.from(cityMap.keys())}
      groupBy={(option) => {
        const stateId = cityMap.get(option)?.state_id;
        if (!stateId) {
          return "";
        }
        return stateMap.get(stateId)?.name || "";
      }}
      value={filterParams.cities?.map((id) => `${id}`)}
      onChange={(_event, newValue) => {
        setFilterParams({
          ...filterParams,
          cities: [...newValue]
            .sort((a, b) => {
              const cityA = cityMap.get(a);
              const cityB = cityMap.get(b);
              return (cityA?.name || "").localeCompare(cityB?.name || "");
            })
            .map((id) => parseInt(id)),
        });
      }}
      renderInput={(params) => <TextField {...params} label="City" />}
      getOptionLabel={(option) => cityMap.get(option)?.name || ""}
      getOptionKey={(option) => cityMap.get(option)?.id || Date.now()}
      disableCloseOnSelect
    />
  );
}
